import { Client } from "@/Common/Api/Client";
import { IPharmacyInfo } from "@/Common/Api/Pharmacy/Dto/Responses/IPharmacyInfo";
import { ObjectHelper } from "@/Common/Helpers/ObjectHelper";
import { FileType } from "@/Common/Models/FileType";
import { Guid } from "@/Common/Models/Guid";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { OverlayElementType } from "@/ExportableFile/Models/OverlayElements/OverlayElement";
import { PrepositionedPharmacyInfoOverlayElement } from "@/ExportableFile/Models/OverlayElements/PrepositionedPharmacyInfoOverlayElement";
import { BaseGetExportableFileFetchUrlResponse } from "../BaseGetExportableFileFetchUrlResponse";
import { ExportableFile, IExportableFile, IExportableFileLoaded } from "../ExportableFile";
import { GenerateFileOptions } from "../IGenerateFileOptions";
import { FileTypeTemporaryFile } from "./FileTypeTemporaryFile";

interface IFileType_Prescription extends IExportableFile {
    prescriptionId: string;
    fromPatientDocId?: string;
    fromPatientDocType?: FileType;
    patientContactId?: string;
    patientContactFacetId?: string;
    supervisorContactId?: Guid | string;
}

class FileTypePrescription extends ExportableFile implements IFileType_Prescription {
    public canGenerateFileXcom: boolean = true;

    public supervisorContactId: Guid = null;
    public prescriptionId: Guid;
    private signAuthorization: SignedPrintAuthorization = null;

    constructor(fields?: IFileType_Prescription) {
        super(FileType.Prescription);
        Object.assign(this, fields);
        this.allowedDraggableImageType = [];
    }

    public getAttachedObject_ObjectId(): string {
        return this.prescriptionId;
    }

    public getCleanCopy(): FileTypePrescription {
        return new FileTypePrescription(this);
    }

    public async getMeta(): Promise<number> {
        await this.generateFileIfNeeded({ includeOverlays: true, isExternal: false });
        return this.totalPages;
    }

    public setSignedPrintAuthorization(signAuthorization: SignedPrintAuthorization): FileTypePrescription {
        this.signAuthorization = signAuthorization;
        return this;
    }

    public hasSignature(): boolean {
        return super.hasSignature()
            || this.signAuthorization != null;
    }

    protected async generateFileIfNeededXcom(includeOverlays: boolean): Promise<IExportableFileLoaded> {
        return await this.generateFileIfNeeded({ includeOverlays: includeOverlays, isExternal: true });
    }

    protected async generateFileIfNeeded(generateFileOption: GenerateFileOptions): Promise<IExportableFileLoaded> {
        const { includeOverlays, isExternal, isFinalFile, exportMode } = generateFileOption;
        let requestedOverlays = (includeOverlays && this.overlayElements) || [];

        const response =
            isExternal
                ? await Client.postAsyncXcom<BaseGetExportableFileFetchUrlResponse>(
                    ServerOperations.Server.ExportableFile_GetPrescriptionFetchUrl,
                    {
                        OverlayElements: this.serializeOverlayElements(),
                        PrescriptionId: this.prescriptionId,
                        IsForQuebec: sessionStore.getCurrentEmployee().isActiveSiteQuebec(),
                        SignedPrintAuthorization: this.signAuthorization,
                        SupervisorContactId: this.supervisorContactId,
                        IsFinalFile: isFinalFile,
                        FileExportMode: exportMode
                    },
                    this.clientTag,
                    this.claimId
                )
                : await Client.postAsync<BaseGetExportableFileFetchUrlResponse>(
                    ServerOperations.Server.ExportableFile_GetPrescriptionFetchUrl,
                    {
                        OverlayElements: this.serializeOverlayElements(),
                        PrescriptionId: this.prescriptionId,
                        IsForQuebec: sessionStore.getCurrentEmployee().isActiveSiteQuebec(),
                        SignedPrintAuthorization: this.signAuthorization,
                        SupervisorContactId: this.supervisorContactId,
                        IsFinalFile: isFinalFile,
                        FileExportMode: exportMode
                    }
                );

        if (!response.isSuccess)
            return;

        this.totalPages = response.data.TotalPages;

        let tempFile = new FileTypeTemporaryFile({
            ObjectId: response.data.ObjectId,
            totalPages: this.totalPages,
            sizeBytes: response.data.SizeBytes,
            date: this.date,
            description: this.description
        });

        tempFile.isExternal = isExternal;
        if (tempFile.isExternal) {
            tempFile.clientTag = this.clientTag;
            tempFile.claimId = this.claimId;
        }

        super.setCache(tempFile, requestedOverlays);

        return {
            OriginalExportableFile: this,
            PregeneratedTemporaryFile: tempFile,
            FetchUrl:
                isExternal
                    ? tempFile.FetchUrlXcom(this.clientTag, this.claimId)
                    : tempFile.FetchUrl
        } as IExportableFileLoaded;
    }

    public async persistSignature(): Promise<void> {
        await Client.postAsync<{ SignedPrintAuthorization: SignedPrintAuthorization }>(
            ServerOperations.Server.Myle_SaveSignedPrintAuthorization,
            { PrescriptionId: this.prescriptionId }
        );
    }

    public trySetPharmarcyInfo(pharmacy: IPharmacyInfo, isSetAsPreferred: boolean): boolean {
        const pharmacyInfoOverlay = this._overlayElements.firstOrDefault(p => p.overlayElementType === OverlayElementType.PrepositionedPharmacyInfo) as PrepositionedPharmacyInfoOverlayElement;
        if (!pharmacyInfoOverlay) {
            this._overlayElements.push(new PrepositionedPharmacyInfoOverlayElement({ Pharmacy: pharmacy, IsSetPreferred: isSetAsPreferred }));
            this.showOverlayRefreshButton = true;
            return true;
        }
        // TODO - Revisit when feature is complete.
        if (!pharmacyInfoOverlay.Pharmacy || !ObjectHelper.equals(pharmacy, pharmacyInfoOverlay.Pharmacy)) {
            pharmacyInfoOverlay.Pharmacy = pharmacy;
            pharmacyInfoOverlay.IsSetPreferred = isSetAsPreferred;
            this.showOverlayRefreshButton = true;
            return true;
        }

        pharmacyInfoOverlay.IsSetPreferred = isSetAsPreferred;
        return false;
    }
}

export { FileTypePrescription };
